import {useCallback, useEffect, useState} from 'react';

interface UseAsyncOptions<T, E extends Error> {
  shouldTrigger?: boolean;
  onSuccess?: (data: T) => void;
  onError?: (error: E) => void;
}

export const useAsync = <T, E extends Error>(
  fn: () => Promise<T>,
  {shouldTrigger = true, onSuccess, onError}: UseAsyncOptions<T, E> = {},
) => {
  const [data, setData] = useState<T | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<E | undefined>(undefined);

  const trigger = useCallback(async () => {
    setError(undefined);
    try {
      const result = await fn();
      setData(result);
      onSuccess?.(result);
    } catch (err) {
      setError(err as E);
      onError?.(err as E);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (shouldTrigger) {
      void trigger();
    }
  }, [trigger]);

  return {data, isLoading, error, trigger};
};
